import type { WidgetConfig, WidgetType } from './schemas';

/** Métadonnées par défaut d'un type de widget : taille initiale, label FR, catégorie. */
export interface WidgetDefault {
  w: number;
  h: number;
  label: string;
  category: string;
}

/**
 * Valeurs par défaut (taille, label, catégorie) de tous les widgets du builder
 * Orion : les 6 widgets cœur (Phase 2a) plus les 11 portés de Metrix (Phase 2b).
 */
export const widgetDefaults: Record<WidgetType, WidgetDefault> = {
  // Tailles par défaut pensées pour la grille 120 colonnes (col ≈ 9px à 1100px,
  // rowHeight 20px) → taille immédiatement exploitable, redimensionnable ensuite.

  // Indicateurs : bandeau de KPI, quatre par ligne (30 × 4 = 120).
  metric_card: { w: 30, h: 6, label: 'Carte métrique', category: 'Indicateurs' },
  comparison_metric: { w: 30, h: 8, label: 'Comparaison', category: 'Indicateurs' },
  gauge: { w: 30, h: 10, label: 'Jauge', category: 'Indicateurs' },
  progress_bar: { w: 30, h: 6, label: 'Barre de progression', category: 'Indicateurs' },

  // Graphiques : deux par ligne (60 × 2 = 120). Le donut est plus étroit et
  // plus haut (rendu circulaire + légende).
  line_chart: { w: 60, h: 12, label: 'Graphique courbe', category: 'Graphiques' },
  area_chart: { w: 60, h: 12, label: 'Graphique aires', category: 'Graphiques' },
  bar_chart: { w: 60, h: 12, label: 'Graphique barres', category: 'Graphiques' },
  donut_pie: { w: 40, h: 14, label: 'Donut', category: 'Graphiques' },

  // Données : le tableau et la heatmap calendrier prennent la pleine largeur
  // (colonnes nombreuses / année entière en abscisse).
  table: { w: 120, h: 12, label: 'Tableau', category: 'Données' },
  top_list: { w: 40, h: 12, label: 'Top liste', category: 'Données' },
  treemap: { w: 60, h: 14, label: 'Treemap', category: 'Données' },
  map: { w: 60, h: 16, label: 'Carte', category: 'Données' },
  calendar_heatmap: { w: 120, h: 10, label: 'Heatmap calendrier', category: 'Données' },

  // Mise en page : blocs statiques, aucune requête de données.
  divider: { w: 120, h: 1, label: 'Séparateur', category: 'Mise en page' },
  image_block: { w: 40, h: 10, label: 'Image', category: 'Mise en page' },
  rich_text: { w: 60, h: 10, label: 'Texte enrichi', category: 'Mise en page' },
  date_range_display: { w: 40, h: 4, label: 'Période affichée', category: 'Mise en page' },
};

/**
 * Widgets dont l'axe est une série temporelle : sans champ temps, le backend ne
 * renvoie qu'une ligne et la courbe se réduit à un point. La heatmap calendrier
 * exige en plus un axe journalier par construction.
 *
 * Les autres widgets n'ont volontairement PAS de granularité par défaut : ils
 * servent le plus souvent une dimension catégorielle (barres par appareil, donut
 * par canal, treemap/carte par zone) et lui imposer une granularité produirait un
 * axe X mixte. Les widgets de mise en page ne consomment aucune donnée.
 */
const TIME_SERIES_WIDGETS: readonly WidgetType[] = ['line_chart', 'area_chart', 'calendar_heatmap'];

/** Config initiale d'un widget fraîchement déposé sur la grille. */
export function defaultWidgetConfig(type: WidgetType): WidgetConfig {
  if (TIME_SERIES_WIDGETS.includes(type)) {
    return { display: { granularity: 'day' } };
  }
  return {};
}

/** Liste des catégories, dans l'ordre d'affichage souhaité dans le picker de widgets. */
export const widgetCategories: { name: string; types: WidgetType[] }[] = [
  { name: 'Indicateurs', types: ['metric_card', 'comparison_metric', 'gauge', 'progress_bar'] },
  { name: 'Graphiques', types: ['line_chart', 'area_chart', 'bar_chart', 'donut_pie'] },
  { name: 'Données', types: ['table', 'top_list', 'treemap', 'map', 'calendar_heatmap'] },
  {
    name: 'Mise en page',
    types: ['divider', 'image_block', 'rich_text', 'date_range_display'],
  },
];
